import { InfoCircleOutlined, RightOutlined, SearchOutlined, TeamOutlined } from 'components/icons';
import { useReducedMotion } from 'hooks/useReducedMotion';
import { CSSProperties, FC, ReactNode, useEffect, useMemo, useState } from 'react';

import Loader from 'components/Loader';
import { Checkbox } from 'components/ui/checkbox';
import { InputGroup, InputGroupAddon, InputGroupInput } from 'components/ui/input-group';
import { Tooltip } from 'components/ui/tooltip';
import { useGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useGetJobRolesListQuery } from 'store/api/jobRoles';
import { EmployeeListItemMinDto, RoleAssociatedUserDto } from 'types/employees';
import { JobRoleGroupType } from 'types/jobRoles';
import { NamedEntityMinDto } from 'types/other';
import { cn } from 'utils/cn';
import { foldForSearch } from 'utils/foldForSearch';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';

// ponytail: one page covers every real tenant (largest firms are a few hundred employees, dev
// has 275). BE Pageable caps at its 2000 default; move to accumulated paging only if a tenant
// ever exceeds it — grouping + mass-select need the whole set in memory anyway.
const FETCH_PAGE_SIZE = 2000;
// Keep the unsaved-changes footer mounted this long after it goes clean so its slide-out, or its fade, runs.
const FOOTER_EXIT_MS = 100;
const GROUP_CLASSIFIER_CODES = ['JOB_ROLE_GROUP_TYPE'];
// Legal first, then Administrative — matching the design's band order.
const BAND_ORDER = [JobRoleGroupType.LEGAL, JobRoleGroupType.ADMIN];

type GroupUser = { id: number; name: string; subtitle: string; selected: boolean; locked: boolean };

type Group = {
    key: string;
    label: string;
    users: GroupUser[]; // visible (search/filter-narrowed) users, sorted by name
    visibleIds: number[];
    total: number;
    selectedTotal: number; // selected among all users in the role, not just visible
    allVisibleSelected: boolean;
    someVisibleSelected: boolean;
    expanded: boolean;
};

type BandSection = {
    key: string;
    label: string;
    groups: Group[];
    visibleIds: number[];
    bandTotal: number;
    bandSelected: number;
    allVisibleSelected: boolean;
    someVisibleSelected: boolean;
};

const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (parts[0][0] + last).toUpperCase();
};

// Classic's rotating avatar fills ride a custom property; the new design draws every avatar in the primary colour.
const AVATAR_COLORS = ['#335131', '#6b8a64', '#5a6b7a', '#8a6f4a', '#4a6b5f', '#7a5a6b'];
const avatarColor = (id: number): string => AVATAR_COLORS[id % AVATAR_COLORS.length];

// Each control states its own colour/weight/size rather than relying on the toolbar's inherited
// text classes, because Preflight resets a raw <button>'s colour and font to `inherit` and the
// toolbar sets neither.
const TAB_BASE = 'h-8 appearance-none rounded-lg border-0 px-3.5 text-[13px] font-semibold transition-all';
const TAB_ON = 'bg-paper text-primary new:shadow-segment shadow-[0_1px_3px_rgba(0,0,0,0.12)]';
const TAB_OFF = 'bg-transparent text-grey-500';
const LINK_BTN =
    'appearance-none border-0 bg-transparent px-1 text-[13px] font-semibold disabled:pointer-events-none disabled:opacity-40';

type JobRoleGroupProps = {
    group: Group;
    onToggleExpand: () => void;
    onToggleGroup: () => void;
    onToggleUser: (id: number) => void;
};

const JobRoleGroup: FC<JobRoleGroupProps> = ({ group, onToggleExpand, onToggleGroup, onToggleUser }) => (
    <div className="new:border-0 new:shadow-card new:outline-hidden bg-paper mx-0.5 my-1.5 overflow-hidden rounded-xl border border-[#eee]">
        <div className="flex items-center gap-2 px-3 py-2">
            <Checkbox
                variant="secondary"
                checked={group.allVisibleSelected}
                indeterminate={group.someVisibleSelected}
                onCheckedChange={() => onToggleGroup()}
                aria-label={t('roles-and-permissions.associated-users-selector.select-all-in-group', {
                    role: group.label,
                })}
            />
            <button
                type="button"
                onClick={onToggleExpand}
                aria-expanded={group.expanded}
                className="flex min-w-0 flex-1 appearance-none items-center gap-2.5 border-0 bg-transparent text-left"
            >
                <RightOutlined
                    className={cn('text-grey-500 transition-transform', group.expanded && 'rotate-90')}
                    style={{ fontSize: 12 }}
                />
                <span className="text-fg text-sm font-semibold">{group.label}</span>
                {group.selectedTotal > 0 && (
                    <span className="text-primary new:bg-primary-wash new:text-caption inline-flex h-5 items-center rounded bg-[#f3f6f1] px-1.5 text-[11px] font-bold">
                        {group.selectedTotal}
                    </span>
                )}
                <span className="text-grey-400 new:text-muted-foreground ml-auto text-xs whitespace-nowrap">
                    {group.selectedTotal} / {group.total}
                </span>
            </button>
        </div>
        {group.expanded && (
            <div className="border-grey-100 border-t">
                {group.users.map((user) => {
                    const row = (
                        <label
                            key={user.id}
                            className={cn(
                                'flex items-center gap-3 px-3 py-1.5 select-none',
                                user.locked ? 'cursor-not-allowed' : 'cursor-pointer',
                                user.selected ? 'new:bg-primary-wash bg-[#f3f6f1]' : 'hover:bg-grey-50'
                            )}
                        >
                            <Checkbox
                                variant="secondary"
                                checked={user.selected}
                                disabled={user.locked}
                                onCheckedChange={() => onToggleUser(user.id)}
                            />
                            <span
                                style={{ '--avatar-bg': avatarColor(user.id) } as CSSProperties}
                                className="new:bg-primary new:text-primary-foreground new:text-caption inline-flex size-[30px] flex-none items-center justify-center rounded-full bg-(--avatar-bg) text-[11px] font-semibold text-white"
                            >
                                {getInitials(user.name)}
                            </span>
                            <div className="min-w-0 flex-1">
                                <div className="text-fg truncate text-sm font-medium">{user.name}</div>
                                {user.subtitle && <div className="text-grey-500 truncate text-xs">{user.subtitle}</div>}
                            </div>
                        </label>
                    );
                    // The tooltip sits on the row, not the checkbox, so hovering anywhere on a locked
                    // member says why its box cannot be cleared.
                    return user.locked ? (
                        <Tooltip key={user.id} content={t('roles-and-permissions.associated-users-selector.only-role')}>
                            {row}
                        </Tooltip>
                    ) : (
                        row
                    );
                })}
            </div>
        )}
    </div>
);

const Band: FC<{ section: BandSection; onToggleBand: () => void; children: ReactNode }> = ({
    section,
    onToggleBand,
    children,
}) => (
    <div className="mt-2">
        <div className="new:border-0 new:shadow-card new:outline-hidden bg-paper sticky top-0 z-[2] mx-0.5 mt-2 mb-1.5 flex items-center gap-[11px] rounded-xl border border-[#eee] px-3 py-[11px]">
            <Checkbox
                variant="secondary"
                checked={section.allVisibleSelected}
                indeterminate={section.someVisibleSelected}
                onCheckedChange={() => onToggleBand()}
                aria-label={t('roles-and-permissions.associated-users-selector.select-all-in-group', {
                    role: section.label,
                })}
            />
            <span className="text-grey-500 new:text-label new:font-semibold new:tracking-[0.06em] text-[11px] font-bold tracking-[0.07em] uppercase">
                {section.label}
            </span>
            <span className="text-grey-400 new:text-muted-foreground text-xs font-medium">
                {t('roles-and-permissions.associated-users-selector.band-selected-count', {
                    count: String(section.bandSelected),
                    total: String(section.bandTotal),
                })}
            </span>
        </div>
        {children}
    </div>
);

type SelectorToolbarProps = {
    search: string;
    onSearchChange: (value: string) => void;
    filter: 'all' | 'selected';
    onFilterChange: (filter: 'all' | 'selected') => void;
    selectedCount: number;
    isSearching: boolean;
    onExpandAll: () => void;
    onCollapseAll: () => void;
    onClear: () => void;
};

const SelectorToolbar: FC<SelectorToolbarProps> = ({
    search,
    onSearchChange,
    filter,
    onFilterChange,
    selectedCount,
    isSearching,
    onExpandAll,
    onCollapseAll,
    onClear,
}) => (
    <div className="border-grey-100 flex flex-wrap items-center gap-3 border-b px-5 py-3">
        <InputGroup className="bg-paper border-grey-300 h-[37px] min-w-60 flex-1 rounded-[10px] md:max-w-md">
            <InputGroupAddon className="pl-3.5">
                <SearchOutlined className="text-grey-500" />
            </InputGroupAddon>
            <InputGroupInput
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={t('roles-and-permissions.associated-users-selector.search-placeholder')}
                aria-label={t('roles-and-permissions.associated-users-selector.search-placeholder')}
            />
        </InputGroup>
        <div className="bg-grey-100 inline-flex rounded-[10px] p-[3px]">
            <button
                type="button"
                onClick={() => onFilterChange('all')}
                className={cn(TAB_BASE, filter === 'all' ? TAB_ON : TAB_OFF)}
            >
                {t('roles-and-permissions.associated-users-selector.all-users')}
            </button>
            <button
                type="button"
                onClick={() => onFilterChange('selected')}
                className={cn(TAB_BASE, filter === 'selected' ? TAB_ON : TAB_OFF)}
            >
                {t('roles-and-permissions.associated-users-selector.selected')} ({selectedCount})
            </button>
        </div>
        <div className="ml-auto flex items-center gap-1">
            <button
                type="button"
                disabled={isSearching}
                className={cn(LINK_BTN, 'text-primary hover:underline')}
                onClick={onExpandAll}
            >
                {t('roles-and-permissions.associated-users-selector.expand-all')}
            </button>
            <span className="text-grey-300">|</span>
            <button
                type="button"
                disabled={isSearching}
                className={cn(LINK_BTN, 'text-primary hover:underline')}
                onClick={onCollapseAll}
            >
                {t('roles-and-permissions.associated-users-selector.collapse-all')}
            </button>
            <span className="text-grey-300">|</span>
            <button
                type="button"
                disabled={selectedCount === 0}
                className={cn(LINK_BTN, 'text-grey-500 hover:text-error')}
                onClick={onClear}
            >
                {t('roles-and-permissions.associated-users-selector.clear')}
            </button>
        </div>
    </div>
);

const ChangesFooter: FC<{ label: string; onReset: () => void; onSave: () => void }> = ({ label, onReset, onSave }) => (
    <div className="associated-users-footer border-divider new:bg-primary-wash flex items-center gap-3.5 border-t bg-[#f3f6f1] px-5 py-3">
        <InfoCircleOutlined className="text-primary flex-none" style={{ fontSize: 18 }} />
        <span className="text-fg text-[13px] font-medium">{label}</span>
        <div className="flex-1" />
        <button
            type="button"
            onClick={onReset}
            className="border-grey-300 text-grey-600 hover:border-grey-500 bg-paper h-[34px] appearance-none rounded-lg border px-3.5 text-[13px] font-semibold"
        >
            {t('roles-and-permissions.associated-users-selector.reset')}
        </button>
        <button
            type="button"
            onClick={onSave}
            className="bg-secondary text-secondary-foreground new:hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] h-[34px] appearance-none rounded-lg border-0 px-[18px] text-[13px] font-semibold hover:bg-[#fa9e2c]"
        >
            {t('roles-and-permissions.associated-users-selector.save-changes')}
        </button>
    </div>
);

type Props = {
    value: NamedEntityMinDto[];
    onChange: (next: NamedEntityMinDto[]) => void;
    // Full DTOs for users already associated with the role. May include inactive employees that
    // are absent from the active-only fetch below; merging them keeps their job-role grouping.
    associatedEmployees: RoleAssociatedUserDto[];
    // The saved baseline the change summary diffs against (updates on save, unlike the query data).
    originalUsers: NamedEntityMinDto[];
    onSave: () => void;
};

const AssociatedUsersSelector: FC<Props> = ({ value, onChange, associatedEmployees, originalUsers, onSave }) => {
    const { data, isLoading } = useGetEmployeesListWithFiltersQuery({ page: 0, pageSize: FETCH_PAGE_SIZE });
    const { data: jobRoles } = useGetJobRolesListQuery();
    const { classifiers } = useClassifiers({ codes: GROUP_CLASSIFIER_CODES });

    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<'all' | 'selected'>('all');
    const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
    const [footer, setFooter] = useState<{ label: string; leaving: boolean } | null>(null);
    const reduceMotion = useReducedMotion();

    const noRoleLabel = t('roles-and-permissions.associated-users-selector.no-job-role');

    // Job role -> band (LEGAL/ADMIN). Employees carry latestEmployment.jobRoleId; the band lives on
    // the job role, so resolve it here rather than plumbing it through the employee DTO.
    const jobRoleBandById = useMemo(() => {
        const byId = new Map<number, JobRoleGroupType>();
        (jobRoles ?? []).forEach((role) => byId.set(role.id, role.groupNameTypeCode));
        return byId;
    }, [jobRoles]);

    // Deduped universe of selectable employees; the active fetch overrides the associated entry so
    // active users get their plain name while inactive-but-associated users still show (with status).
    const universe = useMemo(() => {
        const byId = new Map<number, EmployeeListItemMinDto>();
        associatedEmployees.forEach((employee) => byId.set(employee.id, employee));
        (data?.content ?? []).forEach((employee) => byId.set(employee.id, employee));
        return [...byId.values()];
    }, [data, associatedEmployees]);

    const selectedIds = useMemo(() => new Set((value ?? []).map((user) => user.id)), [value]);

    // Members the backend refuses to unassign because this is their only system role. Kept out of
    // every deselection at the single point all of them go through (individual row, group and band
    // checkboxes, Clear) rather than at each call site.
    const lockedIds = useMemo(
        () => new Set(associatedEmployees.filter((employee) => employee.lastSystemRole).map((e) => e.id)),
        [associatedEmployees]
    );

    const nameById = useMemo(() => new Map(universe.map((e) => [e.id, e.name])), [universe]);

    const emit = (ids: Set<number>) => {
        lockedIds.forEach((id) => ids.add(id));
        onChange(
            [...ids].map((id) => ({ id, name: nameById.get(id) ?? '' })).sort((a, b) => a.name.localeCompare(b.name))
        );
    };

    const toggleUser = (id: number) => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        emit(next);
    };

    // Mass select/deselect a set of ids (a whole role group or a whole band).
    const toggleMany = (ids: number[], allSelected: boolean) => {
        const next = new Set(selectedIds);
        ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
        emit(next);
    };

    const toggleExpand = (group: Group) =>
        setExpandedMap((map) => ({ ...map, [group.key]: !(map[group.key] ?? group.selectedTotal > 0) }));

    const { sections, looseGroups } = useMemo(() => {
        const query = foldForSearch(search.trim());
        const isSel = (e: EmployeeListItemMinDto) => selectedIds.has(e.id);
        const matches = (employee: EmployeeListItemMinDto): boolean => {
            if (filter === 'selected' && !isSel(employee)) return false;
            if (!query) return true;
            const employment = employee.latestEmployment;
            return [employee.name, employment?.jobRoleName, employment?.officeName, employment?.coreTeamName].some(
                (field) => field != null && foldForSearch(field).includes(query)
            );
        };

        const buildGroup = (label: string, all: EmployeeListItemMinDto[]): Group => {
            const visible = all.filter(matches).sort((a, b) => a.name.localeCompare(b.name));
            const selectedVisible = visible.filter(isSel).length;
            const selectedTotal = all.filter(isSel).length;
            return {
                key: label,
                label,
                users: visible.map((e) => ({
                    id: e.id,
                    name: e.name,
                    subtitle: [e.latestEmployment?.officeName, e.latestEmployment?.coreTeamName]
                        .filter(Boolean)
                        .join(' · '),
                    selected: isSel(e),
                    locked: lockedIds.has(e.id),
                })),
                visibleIds: visible.map((e) => e.id),
                total: all.length,
                selectedTotal,
                allVisibleSelected: visible.length > 0 && selectedVisible === visible.length,
                someVisibleSelected: selectedVisible > 0 && selectedVisible < visible.length,
                // Searching force-expands so matches are visible; otherwise default to expanded when
                // the role already has selected members.
                expanded: query !== '' ? true : (expandedMap[label] ?? selectedTotal > 0),
            };
        };

        const buildGroups = (roleMap: Map<string, EmployeeListItemMinDto[]>): Group[] =>
            [...roleMap.entries()]
                .map(([label, all]) => buildGroup(label, all))
                .filter((group) => group.users.length > 0)
                .sort((a, b) => a.label.localeCompare(b.label));

        const push = (map: Map<string, EmployeeListItemMinDto[]>, label: string, employee: EmployeeListItemMinDto) => {
            const bucket = map.get(label);
            if (bucket) bucket.push(employee);
            else map.set(label, [employee]);
        };

        // Partition employees into their band, then by job role. Employees with no job role (or a
        // role not yet resolved while job roles load) fall into a band-less "No job role" bucket.
        const bandRoles = new Map<JobRoleGroupType, Map<string, EmployeeListItemMinDto[]>>();
        const noRoleRoles = new Map<string, EmployeeListItemMinDto[]>();
        universe.forEach((employee) => {
            const jobRoleId = employee.latestEmployment?.jobRoleId ?? null;
            const band = jobRoleId != null ? jobRoleBandById.get(jobRoleId) : undefined;
            const roleLabel = employee.latestEmployment?.jobRoleName?.trim() || noRoleLabel;
            if (band === undefined) {
                push(noRoleRoles, roleLabel, employee);
            } else {
                if (!bandRoles.has(band)) bandRoles.set(band, new Map());
                push(bandRoles.get(band)!, roleLabel, employee);
            }
        });

        const sections = BAND_ORDER.map((code): BandSection | null => {
            const roleMap = bandRoles.get(code);
            if (!roleMap) return null;
            const groups = buildGroups(roleMap);
            if (groups.length === 0) return null;
            const bandAll = [...roleMap.values()].flat();
            const bandVisible = bandAll.filter(matches);
            const selectedVisible = bandVisible.filter(isSel).length;
            return {
                key: code,
                label: getClassifierText(classifiers[GROUP_CLASSIFIER_CODES[0]], code) || code,
                groups,
                visibleIds: bandVisible.map((e) => e.id),
                bandTotal: bandAll.length,
                bandSelected: bandAll.filter(isSel).length,
                allVisibleSelected: bandVisible.length > 0 && selectedVisible === bandVisible.length,
                someVisibleSelected: selectedVisible > 0 && selectedVisible < bandVisible.length,
            };
        }).filter((section): section is BandSection => section !== null);

        return { sections, looseGroups: buildGroups(noRoleRoles) };
    }, [universe, selectedIds, lockedIds, search, filter, expandedMap, jobRoleBandById, classifiers, noRoleLabel]);

    const setAllExpanded = (expanded: boolean) => {
        const keys = [...sections.flatMap((s) => s.groups.map((g) => g.key)), ...looseGroups.map((g) => g.key)];
        setExpandedMap(Object.fromEntries(keys.map((key) => [key, expanded])));
    };

    // While searching, groups are force-expanded to reveal matches, so the expand/collapse controls
    // have no effect — disable them rather than advertise an action they can't perform.
    const isSearching = search.trim() !== '';
    const isEmpty = sections.length === 0 && looseGroups.length === 0;

    // Diff current selection against the saved baseline for the unsaved-changes summary.
    const originalIds = new Set(originalUsers.map((user) => user.id));
    const added = [...selectedIds].filter((id) => !originalIds.has(id)).length;
    const removed = originalUsers.filter((user) => !selectedIds.has(user.id)).length;
    const changeParts: string[] = [];
    if (added > 0) {
        const key = added === 1 ? 'changes-add-one' : 'changes-add-many';
        changeParts.push(t(`roles-and-permissions.associated-users-selector.${key}`, { count: String(added) }));
    }
    if (removed > 0) {
        changeParts.push(
            t('roles-and-permissions.associated-users-selector.changes-remove', { count: String(removed) })
        );
    }
    const changeLabel = `${changeParts.join(' · ')} — ${t('roles-and-permissions.associated-users-selector.unsaved-changes')}`;

    // Presence-manage the footer so it can slide out, or fade out under reduced motion in the new design: while there
    // are changes it holds the current label; once the selection returns to the saved baseline it is briefly kept
    // mounted (holding the last label, flagged `leaving`, so the label doesn't flash empty) while it leaves, then
    // unmounted. Adjusting state during render (guarded so it settles) is the standard
    // "derive on prop change" pattern.
    const dirty = changeParts.length > 0;
    if (dirty) {
        if (!footer || footer.label !== changeLabel || footer.leaving) {
            setFooter({ label: changeLabel, leaving: false });
        }
    } else if (footer && !footer.leaving) {
        setFooter({ label: footer.label, leaving: true });
    }
    useEffect(() => {
        if (!footer?.leaving) return;
        // Under reduced motion classic drops it at once; the new design fades it out where it stands.
        const goesAtOnce = reduceMotion && document.documentElement.dataset.design !== 'new';
        const timer = setTimeout(() => setFooter(null), goesAtOnce ? 0 : FOOTER_EXIT_MS);
        return () => clearTimeout(timer);
    }, [footer, reduceMotion]);

    const renderGroup = (group: Group) => (
        <JobRoleGroup
            key={group.key}
            group={group}
            onToggleExpand={() => toggleExpand(group)}
            onToggleGroup={() => toggleMany(group.visibleIds, group.allVisibleSelected)}
            onToggleUser={toggleUser}
        />
    );

    return (
        <div className="border-grey-a800 bg-paper new:border-0 new:shadow-card new:outline-hidden overflow-hidden rounded-[20px] border shadow-[0_1px_4px_rgba(20,33,26,0.08)]">
            <div className="border-divider flex items-start justify-between gap-4 border-b px-5 py-4">
                <div>
                    <div className="text-fg text-base font-semibold">{t('roles-and-permissions.associated-users')}</div>
                    <div className="text-grey-500 mt-0.5 text-xs">
                        {t('roles-and-permissions.associated-users-selector.grouped-hint')}
                    </div>
                </div>
                <span className="text-primary new:bg-primary-wash inline-flex h-7 flex-none items-center gap-1.5 rounded-lg bg-[#f3f6f1] px-3 text-[13px] font-semibold">
                    <TeamOutlined />
                    {t('roles-and-permissions.associated-users-selector.selected-count', {
                        count: String(selectedIds.size),
                    })}
                </span>
            </div>

            <SelectorToolbar
                search={search}
                onSearchChange={setSearch}
                filter={filter}
                onFilterChange={setFilter}
                selectedCount={selectedIds.size}
                isSearching={isSearching}
                onExpandAll={() => setAllExpanded(true)}
                onCollapseAll={() => setAllExpanded(false)}
                onClear={() => emit(new Set())}
            />

            <div className="max-h-[48vh] overflow-auto px-3 pt-1 pb-3">
                {isLoading && universe.length === 0 ? (
                    <Loader />
                ) : isEmpty ? (
                    <div className="text-grey-400 flex flex-col items-center gap-2 px-5 py-11">
                        <SearchOutlined style={{ fontSize: 26 }} />
                        <div className="text-grey-500 text-sm">
                            {t('roles-and-permissions.associated-users-selector.no-results')}
                        </div>
                    </div>
                ) : (
                    <>
                        {sections.map((section) => (
                            <Band
                                key={section.key}
                                section={section}
                                onToggleBand={() => toggleMany(section.visibleIds, section.allVisibleSelected)}
                            >
                                {section.groups.map(renderGroup)}
                            </Band>
                        ))}
                        {looseGroups.map(renderGroup)}
                    </>
                )}
            </div>

            {footer && (
                <div
                    className={cn(
                        'new:motion-reduce:transition-opacity grid transition-[grid-template-rows,opacity] duration-100 ease-out',
                        footer.leaving
                            ? 'new:motion-reduce:grid-rows-[1fr] grid-rows-[0fr] opacity-0'
                            : 'grid-rows-[1fr] opacity-100'
                    )}
                >
                    <div className="overflow-hidden">
                        <ChangesFooter label={footer.label} onReset={() => onChange(originalUsers)} onSave={onSave} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssociatedUsersSelector;
